<script setup lang="ts">
// Previews of the frames a scan captured. The bucket is private, so each frame is fetched through
// the signed API as a blob and turned into an object URL this component owns and revokes.

import { computed, onBeforeUnmount, ref, watch, type PropType } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import type { IPerfusionScanFrame, PerfusionScanModel } from '@/models/perfusionScan';
import PerfusionScanService from '@/services/perfusionScanService';

const props = defineProps({
  scan: {
    type: Object as PropType<PerfusionScanModel>,
    required: true
  },
  pumpCenterId: {
    type: String,
    required: true
  },
  perfusionId: {
    type: String,
    required: true
  },
  /** Caps how many frames are previewed — the case list shows one, the detail view shows all. */
  maxFrames: {
    type: Number,
    default: undefined
  },
  /** Small tiles for the list; the detail view uses the full grid. */
  compact: {
    type: Boolean,
    default: false
  },
  /** Whether clicking a frame opens it full-resolution. Off inside a clickable card. */
  interactive: {
    type: Boolean,
    default: true
  }
});

type FrameTile = {
  index: number;
  name: string;
  size?: number;
  isImage: boolean;
  url?: string;
  loading: boolean;
  failed: boolean;
};

/** Thumbnails are requested at 2x their display size so they stay sharp on retina screens. */
const THUMBNAIL_WIDTH = 220;
const COMPACT_THUMBNAIL_WIDTH = 96;

/** Frames are fetched a few at a time, so a twelve-frame scan doesn't open twelve requests. */
const LOAD_CONCURRENCY = 3;

const tiles = ref<FrameTile[]>([]);
const activeIndex = ref<number | undefined>();
const fullUrls = ref<Record<number, string>>({});
const fullLoading = ref(false);
const fullFailed = ref(false);

const thumbnailWidth = computed(() => (props.compact ? COMPACT_THUMBNAIL_WIDTH : THUMBNAIL_WIDTH));

const frameName = (frame: IPerfusionScanFrame, index: number) =>
  frame.originalName || frame.key?.split('/').pop() || `Frame ${index + 1}`;

const isImageFrame = (frame: IPerfusionScanFrame) =>
  !frame.contentType || `${frame.contentType}`.startsWith('image/');

const activeTile = computed(() =>
  activeIndex.value === undefined ?
    undefined :
    tiles.value.find(tile => tile.index === activeIndex.value)
);

const activeUrl = computed(() =>
  activeIndex.value === undefined ? undefined : fullUrls.value[activeIndex.value]
);

const formatSize = (size?: number) =>
  typeof size === 'number' && Number.isFinite(size) ?
    `${Math.max(Math.round(size / 1024), 1)} KB` :
    '';

const revokeUrls = () => {
  tiles.value.forEach(tile => {
    if (tile.url) {
      window.URL.revokeObjectURL(tile.url);
    }
  });
  Object.values(fullUrls.value).forEach(url => window.URL.revokeObjectURL(url));
  fullUrls.value = {};
};

// Only the newest load may write to `tiles`, so switching scans mid-flight can't mix frames.
let activeLoadId = 0;

const loadThumbnail = async (tile: FrameTile, loadId: number) => {
  try {
    const url = await PerfusionScanService.getFrameUrl(
      props.pumpCenterId,
      props.perfusionId,
      props.scan.id,
      tile.index,
      thumbnailWidth.value
    );

    if (loadId !== activeLoadId) {
      window.URL.revokeObjectURL(url);
      return;
    }

    tile.url = url;
  } catch {
    if (loadId !== activeLoadId) {
      return;
    }

    // A frame that won't load is shown as a failed tile rather than a toast: the values on the
    // scan are still worth reading, and one bad object shouldn't look like a broken page.
    tile.failed = true;
  } finally {
    if (loadId === activeLoadId) {
      tile.loading = false;
    }
  }
};

const loadThumbnails = async () => {
  const loadId = activeLoadId;
  const pending = tiles.value.filter(tile => tile.isImage);

  const workers = Array.from({ length: Math.min(LOAD_CONCURRENCY, pending.length) }, async () => {
    let next = pending.shift();
    while (next) {
      await loadThumbnail(next, loadId);
      if (loadId !== activeLoadId) {
        return;
      }
      next = pending.shift();
    }
  });

  await Promise.all(workers);
};

const reset = () => {
  activeLoadId++;
  revokeUrls();
  activeIndex.value = undefined;
  fullLoading.value = false;
  fullFailed.value = false;

  const frames = props.scan.frames ?? [];
  const visible = props.maxFrames === undefined ? frames : frames.slice(0, props.maxFrames);

  tiles.value = visible.map((frame, index) => ({
    // The endpoint addresses frames by their position in the stored array, not by the descriptor's
    // own `index` field, so the position is what gets requested.
    index,
    name: frameName(frame, index),
    size: frame.size,
    isImage: isImageFrame(frame),
    loading: isImageFrame(frame),
    failed: false
  }));

  if (tiles.value.length > 0) {
    void loadThumbnails();
  }
};

watch(
  () => [props.scan.id, props.pumpCenterId, props.perfusionId] as const,
  () => reset(),
  { immediate: true }
);

const retry = (tile: FrameTile) => {
  tile.failed = false;
  tile.loading = true;
  void loadThumbnail(tile, activeLoadId);
};

const openFrame = async (tile: FrameTile) => {
  if (!props.interactive || !tile.isImage || tile.failed) {
    return;
  }

  activeIndex.value = tile.index;
  fullFailed.value = false;

  if (fullUrls.value[tile.index]) {
    return;
  }

  const loadId = activeLoadId;
  fullLoading.value = true;
  try {
    const url = await PerfusionScanService.getFrameUrl(
      props.pumpCenterId,
      props.perfusionId,
      props.scan.id,
      tile.index
    );

    if (loadId !== activeLoadId) {
      window.URL.revokeObjectURL(url);
      return;
    }

    fullUrls.value = { ...fullUrls.value, [tile.index]: url };
  } catch {
    if (loadId === activeLoadId) {
      fullFailed.value = true;
    }
  } finally {
    if (loadId === activeLoadId) {
      fullLoading.value = false;
    }
  }
};

const closeFrame = () => {
  activeIndex.value = undefined;
  fullFailed.value = false;
};

const showNeighbour = (step: number) => {
  const openable = tiles.value.filter(tile => tile.isImage && !tile.failed);
  const position = openable.findIndex(tile => tile.index === activeIndex.value);
  const next = openable[position + step];
  if (next) {
    void openFrame(next);
  }
};

// Listened for on the window: the overlay is teleported, so it never owns focus by default and
// would otherwise miss Escape entirely.
const handleLightboxKeys = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeFrame();
  } else if (event.key === 'ArrowLeft') {
    showNeighbour(-1);
  } else if (event.key === 'ArrowRight') {
    showNeighbour(1);
  }
};

watch(activeIndex, index => {
  if (index === undefined) {
    window.removeEventListener('keydown', handleLightboxKeys);
  } else {
    window.addEventListener('keydown', handleLightboxKeys);
  }
});

onBeforeUnmount(() => {
  activeLoadId++;
  window.removeEventListener('keydown', handleLightboxKeys);
  revokeUrls();
});
</script>

<template>
  <div class="frames" :class="{ compact }" v-if="tiles.length > 0">
    <div class="frame-tile"
      v-for="tile in tiles"
      :key="tile.index"
      :class="{ clickable: interactive && tile.isImage && !tile.failed }"
      :role="interactive && tile.isImage && !tile.failed ? 'button' : undefined"
      :tabindex="interactive && tile.isImage && !tile.failed ? 0 : undefined"
      :aria-label="interactive && tile.isImage && !tile.failed ? `Open ${tile.name} full size` : undefined"
      @click.stop="openFrame(tile)"
      @keydown.enter.prevent="openFrame(tile)"
      @keydown.space.prevent="openFrame(tile)">
      <div class="frame-thumb">
        <ProgressSpinner v-if="tile.loading" strokeWidth="4" class="frame-spinner"/>
        <img v-else-if="tile.url"
          :src="tile.url"
          :alt="tile.name"
          class="frame-image"/>
        <button v-else-if="tile.failed"
          type="button"
          class="frame-retry"
          @click.stop="retry(tile)">
          <FontAwesomeIcon :icon="['far', 'rotate-exclamation']"/>
          <span v-if="!compact">Retry</span>
        </button>
        <FontAwesomeIcon v-else :icon="['far', 'file']" class="frame-file-icon"/>
      </div>
      <div class="frame-caption" v-if="!compact">
        <span class="frame-name">{{ tile.name }}</span>
        <span class="frame-size" v-if="formatSize(tile.size)">{{ formatSize(tile.size) }}</span>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div class="frame-lightbox"
      v-if="activeIndex !== undefined"
      role="dialog"
      aria-modal="true"
      :aria-label="activeTile?.name"
      @click="closeFrame">
      <div class="frame-lightbox__body" @click.stop>
        <div class="frame-lightbox__bar">
          <span class="frame-lightbox__name">{{ activeTile?.name }}</span>
          <button type="button"
class="frame-lightbox__close"
aria-label="Close"
@click="closeFrame">
            <FontAwesomeIcon :icon="['far', 'xmark']"/>
          </button>
        </div>
        <div class="frame-lightbox__stage">
          <ProgressSpinner v-if="fullLoading" strokeWidth="4" style="width: 48px; height: 48px"/>
          <img v-else-if="activeUrl"
:src="activeUrl"
:alt="activeTile?.name"
class="frame-lightbox__image"/>
          <p v-else-if="fullFailed" class="frame-lightbox__error">
            Couldn't load this frame — try again.
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
@use '../../../assets/variables.module' as *;

.frames {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 10px;

  &.compact {
    display: flex;
    gap: 6px;
  }
}

.frame-tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  &.clickable {
    cursor: pointer;

    &:hover .frame-thumb,
    &:focus-visible .frame-thumb {
      border-color: var(--p-primary-color, #{$primary-color});
      outline: none;
    }
  }
}

.frame-thumb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 8px;
  background: hsl(210, 20%, 97%);
  transition: border-color 0.15s ease;
}

.frame-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.frame-spinner {
  width: 24px;
  height: 24px;
}

.frame-file-icon {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.35);
}

.frame-retry {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: hsl(0, 65%, 45%);
  font: inherit;
  font-size: 11px;
  cursor: pointer;
}

.frame-caption {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  font-size: 11px;
  color: hsl(210, 9%, 50%);
  min-width: 0;
}

.frame-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.frame-size {
  flex-shrink: 0;
}

.compact {
  .frame-thumb {
    width: 48px;
    aspect-ratio: 1 / 1;
    border-radius: 6px;
  }
}

.frame-lightbox {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(15, 15, 15, 0.72);
}

.frame-lightbox__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: min(1100px, 100%);
  max-height: 100%;
}

.frame-lightbox__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: white;
}

.frame-lightbox__name {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.frame-lightbox__close {
  border: none;
  background: transparent;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
}

.frame-lightbox__stage {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 240px;
  min-height: 240px;
  overflow: auto;
}

.frame-lightbox__image {
  max-width: 100%;
  max-height: calc(100vh - 140px);
  object-fit: contain;
  border-radius: 8px;
  background: white;
}

.frame-lightbox__error {
  margin: 0;
  color: white;
  font-size: 13px;
}
</style>
